import { FormEvent, useEffect, useRef, useState } from 'react'
import { ArrowRight, Building2, Eye, EyeOff, MessageSquareText, ShieldCheck } from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, clearCredentials } from '../lib/api'
import { isFeishuClient } from '../lib/runtimeContext'
import { FeishuOauthLink } from '../components/FeishuOauthLink'
import type { FeishuIntegrationStatus } from '../types'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [feishu, setFeishu] = useState<FeishuIntegrationStatus>()
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [error, setError] = useState(searchParams.get('feishuError') || '')
  const [loading, setLoading] = useState(false)
  const feishuRecoveryTimer = useRef<number | undefined>(undefined)
  const feishuClient = isFeishuClient(window.location.search, window.navigator.userAgent)

  useEffect(() => {
    api.session().then(session => { if (session.authenticated) navigate('/') }).catch(() => undefined)
    api.feishuStatus().then(setFeishu).catch(() => undefined)
  }, [navigate])

  useEffect(() => () => {
    if (feishuRecoveryTimer.current) window.clearTimeout(feishuRecoveryTimer.current)
  }, [])

  function monitorFeishuLoginCompletion() {
    if (feishuRecoveryTimer.current) window.clearTimeout(feishuRecoveryTimer.current)
    const deadline = Date.now() + 120_000
    const check = async () => {
      try {
        const session = await api.session()
        if (session.authenticated) {
          window.location.replace('/')
          return
        }
      } catch {
        // The OAuth page may temporarily suspend same-origin requests while changing documents.
      }
      if (Date.now() < deadline) feishuRecoveryTimer.current = window.setTimeout(check, 800)
    }
    feishuRecoveryTimer.current = window.setTimeout(check, 800)
  }

  async function submit(event: FormEvent) {
    event.preventDefault(); setLoading(true); setError(''); clearCredentials()
    try {
      const session = await api.login(username, password)
      if (!session.authenticated) throw new Error('用户名或密码错误')
      navigate('/')
    } catch (e) { setError(e instanceof Error ? e.message : '登录失败') }
    finally { setLoading(false) }
  }

  return <main className="login-page">
    <section className="login-card">
      <div className="login-brand"><span><ShieldCheck size={23} /></span><div><b>License Center</b><small>数巅科技授权管理平台</small></div></div>
      <form onSubmit={submit}><div className="form-heading"><h1>欢迎回来</h1><p>业务人员和管理员统一登录</p></div>{searchParams.get('passwordChanged') === '1' && <div className="success-banner">密码已更新，请使用新密码登录。</div>}<label>账号<input autoFocus value={username} onChange={e => setUsername(e.target.value)} required placeholder="请输入账号" /></label><label>密码<span className="password-field"><input type={passwordVisible ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="请输入密码" required /><button type="button" className="password-toggle" onClick={() => setPasswordVisible(visible => !visible)} aria-label={passwordVisible ? '隐藏密码' : '显示密码'} title={passwordVisible ? '隐藏密码' : '显示密码'}>{passwordVisible ? <EyeOff size={17} /> : <Eye size={17} />}</button></span></label>{error && <div className="form-error">{error}</div>}<button className="button button--primary button--wide" disabled={loading}>{loading ? '正在登录...' : '登录'}<ArrowRight size={18} /></button><div className="login-note"><ShieldCheck size={15} />账号由管理员统一创建和维护</div></form>
      {feishu?.oauthReady && <FeishuOauthLink className="login-feishu-entry" href={feishu.loginUrl} onNavigate={monitorFeishuLoginCompletion}><span><MessageSquareText size={18} /></span><div><b>使用飞书企业身份登录</b><small>适用于已完成内部岗位绑定的员工</small></div><ArrowRight size={16} /></FeishuOauthLink>}
      {!feishuClient && <aside className="login-portal-entry" aria-label="客户与渠道用户入口"><span><Building2 size={17} /></span><div><b>客户与渠道自助门户</b><p>查看授权权益、下载 License、跟踪激活与服务请求。</p></div><Link to="/portal/login">进入门户<ArrowRight size={14} /></Link></aside>}
    </section>
  </main>
}
